import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  ClipboardList,
  Archive,
  LineChart,
  Upload,
  ShoppingBag,
  AlertTriangle,
  CalendarDays,
  Zap,
  Search,
  PieChart,
  Activity,
  FileBarChart,
  Cog,
  Package,
  BarChart3,
  Settings,
  TrendingUp,
  Megaphone,
  Database,
  Truck,
  Building2,
  DollarSign
} from 'lucide-react';
import { toast } from 'sonner';
import { useLanguage } from '@/contexts/LanguageContext';
import AutoHideSidebar from '@/components/AutoHideSidebar';
import CompanySwitcher from '@/components/CompanySwitcher';
import ThemeToggle from '@/components/ThemeToggle';
import { useTheme } from '@/hooks/useTheme';
import { ROUTES } from '@/lib/routes';
import LoadingSpinner from '@/components/LoadingSpinner';
import { useCompanyFeatureFlag } from '@/hooks/useCompanyFeatureFlag';
import { ShoppingCart as HQPurchasingIcon, Truck, Calculator } from 'lucide-react';

interface ModuleItem {
  id: string;
  titleKey: string;
  icon: React.ComponentType<any>;
  category: string;
  descriptionKey: string;
  badge?: string;
  route?: string;
}

const Dashboard = () => {
  const [userEmail, setUserEmail] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('order');
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { isLoading: themeLoading, isInitialized: themeInitialized } = useTheme();
  const { companyId } = useParams();

  const modules: ModuleItem[] = [
    // Order Management
    { id: 'order-overview', titleKey: 'orderOverview', icon: ClipboardList, category: 'order', descriptionKey: 'orderOverview', route: ROUTES.order.overview },
    { id: 'sales-stats', titleKey: 'salesStats', icon: BarChart3, category: 'order', descriptionKey: 'salesStats', route: ROUTES.order.statistics },
    { id: 'sales-import', titleKey: 'salesImport', icon: Upload, category: 'order', descriptionKey: 'salesImport', route: ROUTES.order.import },
    
    // Product Overview
    { id: 'product-mgmt', titleKey: 'productMgmt', icon: Package, category: 'product', descriptionKey: 'productMgmt', route: ROUTES.product.overview.management },
    { id: 'inventory-mgmt', titleKey: 'inventoryMgmt', icon: Archive, category: 'product', descriptionKey: 'inventoryMgmt', route: ROUTES.product.overview.stock },
    { id: 'product-stats', titleKey: 'productStats', icon: PieChart, category: 'product', descriptionKey: 'productStats', route: ROUTES.product.overview.statistics },
    { id: 'product-import', titleKey: 'productImport', icon: Upload, category: 'product', descriptionKey: 'productImport', route: ROUTES.product.overview.import },
    { id: 'stock-import', titleKey: 'productStockImport', icon: Upload, category: 'product', descriptionKey: 'productStockImport', route: ROUTES.product.overview.importStock },
    { id: 'pending-import', titleKey: 'productPendingImport', icon: Database, category: 'product', descriptionKey: 'productPendingImport', route: ROUTES.product.overview.importPending },
    { id: '3pl-import', titleKey: 'product3PLDataImport', icon: Truck, category: 'product', descriptionKey: 'product3PLDataImport', route: ROUTES.product.overview.import3PL },
    
    // Purchase Suggestions
    { id: 'suggested-orders', titleKey: 'suggestedOrders', icon: ShoppingBag, category: 'purchase', descriptionKey: 'suggestedOrders', route: ROUTES.purchase.suggestedOrders },
    { id: 'cross-company-suggestions', titleKey: 'crossCompanySuggestions', icon: Building2, category: 'purchase', descriptionKey: 'crossCompanySuggestions', route: ROUTES.purchase.crossCompanySuggestions },
    { id: 'rule-settings', titleKey: 'ruleSettings', icon: Settings, category: 'purchase', descriptionKey: 'ruleSettings', route: ROUTES.purchase.ruleSettings },
    
    // Expiry Summary
    { id: 'expiring-soon', titleKey: 'expiringSoon', icon: AlertTriangle, category: 'expiry', descriptionKey: 'expiringSoon', badge: 'Alert', route: ROUTES.expiry.expiringSoon },
    { id: 'expiry-handling', titleKey: 'expiryHandling', icon: CalendarDays, category: 'expiry', descriptionKey: 'expiryHandling', route: ROUTES.expiry.expiryProcessing },
    { id: 'expiry-import', titleKey: 'bestExpiryImport', icon: Upload, category: 'expiry', descriptionKey: 'bestExpiryImport', route: ROUTES.expiry.bestExpiryImport },
    
    // Promotions
    { id: 'campaign-overview', titleKey: 'campaignOverview', icon: Megaphone, category: 'promotion', descriptionKey: 'campaignOverview' },
    { id: 'search-campaigns', titleKey: 'searchCampaigns', icon: Search, category: 'promotion', descriptionKey: 'searchCampaigns' },
    { id: 'promotion-import', titleKey: 'promotionImport', icon: Upload, category: 'promotion', descriptionKey: 'promotionImport' },
    
    // Analytics
    { id: 'sales-analysis', titleKey: 'salesAnalysis', icon: LineChart, category: 'analytics', descriptionKey: 'salesAnalysis', route: ROUTES.analytics.salesAnalysis },
    { id: 'stock-analysis', titleKey: 'stockAnalysis', icon: FileBarChart, category: 'analytics', descriptionKey: 'stockAnalysis', route: ROUTES.product.overview.statistics },
    { id: 'slow-moving', titleKey: 'slowMoving', icon: Activity, category: 'analytics', descriptionKey: 'slowMoving', route: ROUTES.analytics.slowMoving },
    { id: 'traffic-analysis', titleKey: 'trafficAnalysis', icon: TrendingUp, category: 'analytics', descriptionKey: 'trafficAnalysis', route: ROUTES.analytics.trafficAnalysis },
    { id: 'price-analysis', titleKey: 'priceAnalysis', icon: DollarSign, category: 'analytics', descriptionKey: 'priceAnalysis', route: ROUTES.analytics.priceAnalysis },
    
    // Store Settings
    { id: 'store-settings', titleKey: 'storeMgmt', icon: Cog, category: 'settings', descriptionKey: 'storeMgmt' },

    // Headquarters (only included when company has headquarters_module flag)
    { id: 'hq-purchasing', titleKey: 'hqPurchasing', icon: HQPurchasingIcon, category: 'hq', descriptionKey: 'hqPurchasing', route: ROUTES.hq.purchasing },
    { id: 'hq-wholesale', titleKey: 'hqWholesale', icon: Truck, category: 'hq', descriptionKey: 'hqWholesale', route: ROUTES.hq.wholesale },
    { id: 'hq-accounting', titleKey: 'hqAccounting', icon: Calculator, category: 'hq', descriptionKey: 'hqAccounting', route: ROUTES.hq.accounting },
  ];

  const { enabled: hqEnabled } = useCompanyFeatureFlag();

  const baseCategories = [
    { id: 'order', titleKey: 'orderManagement', subtitleKey: 'orderManagement' },
    { id: 'product', titleKey: 'productOverview', subtitleKey: 'productOverview' },
    { id: 'purchase', titleKey: 'purchaseSuggestions', subtitleKey: 'purchaseSuggestions' },
    { id: 'expiry', titleKey: 'expirySummary', subtitleKey: 'expirySummary' },
    { id: 'promotion', titleKey: 'promotions', subtitleKey: 'promotions' },
    { id: 'analytics', titleKey: 'analytics', subtitleKey: 'analytics' },
    { id: 'settings', titleKey: 'storeSettings', subtitleKey: 'storeSettings' },
  ];

  const categories = hqEnabled
    ? [...baseCategories, { id: 'hq', titleKey: 'headquarters', subtitleKey: 'headquarters' }]
    : baseCategories;

  useEffect(() => {
    const email = localStorage.getItem('userEmail');
    let company = localStorage.getItem('selectedCompanyName');
    if (companyId) {
      company = company || `公司ID: ${companyId}`;
    }
    if (!email || (!company && !companyId)) {
      navigate('/login');
      return;
    }
    setUserEmail(email);
    setCompanyName(company || '');
    
    // 从 localStorage 读取保存的分类，如果存在则使用它
    const savedCategory = localStorage.getItem('selectedCategory');
    if (savedCategory) {
      const isValidCategory = categories.some(c => c.id === savedCategory);
      if (isValidCategory) {
        setSelectedCategory(savedCategory);
        // 清除 localStorage 中的分类，避免下次进入时重复使用
        localStorage.removeItem('selectedCategory');
      }
    }
  }, [navigate, companyId]);

  // 确保 selectedCategory 始终有效
  useEffect(() => {
    const isValidCategory = categories.some(c => c.id === selectedCategory);
    if (!isValidCategory && selectedCategory !== 'order') {
      // 如果当前选中的分类无效，重置为默认值
      setSelectedCategory('order');
    }
  }, [selectedCategory]);

  const handleModuleClick = (module: ModuleItem) => {
    if (module.route) {
      // Prefer hard navigation to avoid any SPA cache/state issues
      window.location.href = module.route;
      return;
    }
    if (import.meta.env.DEV) {
      toast.error(`Missing route for module: ${module.id}`);
    } else {
      toast.info(`Opening ${t(module.titleKey)}...`);
    }
  };

  const filteredModules = modules.filter(module => module.category === selectedCategory);
  
  // 确保 selectedCategory 始终有效，如果无效则使用默认值
  const validCategory = categories.find(c => c.id === selectedCategory) ? selectedCategory : 'order';
  const currentCategory = categories.find(c => c.id === validCategory);

  // Show loading spinner until theme is initialized to prevent flash
  if (!themeInitialized || themeLoading) {
    return <LoadingSpinner fullScreen text="Loading theme..." />;
  }

  return (
    <div className="min-h-screen bg-background flex">
      {/* Auto-Hide Sidebar */}
      <AutoHideSidebar 
        selectedCategory={validCategory}
        setSelectedCategory={setSelectedCategory}
        userEmail={userEmail}
        companyName={companyName}
      />

      {/* Main Content */}
      <div className="flex-1 flex flex-col">
        {/* Header */}
        <div className="bg-card/50 border-b border-border p-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent">
                {currentCategory ? t(currentCategory.titleKey) : ''}
              </h1>
              <p className="text-muted-foreground">
                {currentCategory ? t(currentCategory.subtitleKey) : ''}
              </p>
            </div>
            <div className="flex items-center gap-4">
              <CompanySwitcher 
                showManagementButton={true}
                onCompanyChange={(company) => {
                  setCompanyName(company.name);
                  // Update localStorage for immediate reflection
                  localStorage.setItem('selectedCompanyName', company.name);
                }}
              />
              {/* <ThemeToggle /> */}
              <div className="text-right">
                <p className="text-foreground font-medium">{userEmail}</p>
                <p className="text-muted-foreground text-sm">{companyName}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Content Area */}
        <div className="flex-1 p-6 bg-background">
          {filteredModules.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {filteredModules.map((module) => {
              const IconComponent = module.icon;
              
              return (
                <Card
                  key={module.id}
                  className="group cursor-pointer transition-all duration-300 hover:scale-105 bg-card/80 border-border hover:border-purple-500/50 hover:shadow-2xl hover:shadow-purple-500/20"
                  onClick={() => handleModuleClick(module)}
                >
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <div className="w-12 h-12 bg-gradient-to-br from-purple-600 to-cyan-600 rounded-xl flex items-center justify-center group-hover:shadow-lg group-hover:shadow-purple-500/30 transition-all duration-300">
                        <IconComponent className="w-6 h-6 text-white" />
                      </div>
                      {module.badge && (
                        <Badge variant="secondary" className="bg-purple-600/20 text-purple-400 border-purple-600/30">
                          {module.badge}
                        </Badge>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent>
                    <CardTitle className="text-foreground text-lg mb-2 group-hover:text-purple-500 transition-colors">
                      {t(module.titleKey)}
                    </CardTitle>
                    <p className="text-muted-foreground text-sm">
                      {t(module.descriptionKey)}
                    </p>
                  </CardContent>
                </Card>
              );
            })}
            </div>
          ) : (
            <div className="flex items-center justify-center h-full">
              <Card className="bg-card/80 border-border p-8">
                <CardContent className="text-center">
                  <p className="text-muted-foreground text-lg">
                    {t('noModulesAvailable') || '暫無可用功能'}
                  </p>
                  <p className="text-muted-foreground/80 text-sm mt-2">
                    {t('selectAnotherCategory') || '請選擇其他分類'}
                  </p>
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
